<template>
  <div class="inline-flex items-start gap-2">
    <!-- Left container - Rating buttons -->
    <div class="flex items-center gap-1.5">
      <button
        type="button"
        :class="[
          'flex h-8 items-center justify-center rounded-md p-1.5 transition-colors',
          rating === 'positive'
            ? 'bg-purple-50 text-purple-600'
            : 'text-gray-500 hover:text-purple-600'
        ]"
        :aria-label="rating === 'positive' ? t('MarkedHelpful') : t('Helpful')"
        @click="handleRating('positive')"
      >
        <ThumbsUp
          class="h-5 w-5"
          :stroke-width="1.5"
        />
      </button>
      <button
        type="button"
        :class="[
          'flex h-8 items-center justify-center rounded-md p-1.5 transition-colors',
          rating === 'negative'
            ? 'bg-purple-50 text-purple-600'
            : 'text-gray-500 hover:text-purple-600'
        ]"
        :aria-label="rating === 'negative' ? t('MarkedNotHelpful') : t('NotHelpful')"
        @click="handleRating('negative')"
      >
        <ThumbsDown
          class="h-5 w-5"
          :stroke-width="1.5"
        />
      </button>
    </div>

    <!-- Right container - Copy + More popover -->
    <div class="flex items-center">
      <button
        type="button"
        class="flex h-8 items-center justify-center rounded-md p-1.5 text-gray-500 transition-colors
          hover:text-purple-600"
        :aria-label="copyButtonTitle"
        @click="copyToClipboard"
      >
        <transition
          name="fade"
          mode="out-in"
        >
          <Check
            v-if="copied"
            key="check-icon"
            class="h-5 w-5 text-purple-600"
            :stroke-width="1.5"
          />
          <Copy
            v-else
            key="copy-icon"
            class="h-5 w-5"
            :stroke-width="1.5"
          />
        </transition>
      </button>

      <Popover v-model:open="morePopoverOpen">
        <PopoverTrigger as-child>
          <button
            type="button"
            class="flex h-8 items-center justify-center rounded-md p-1.5 text-gray-500 transition-colors
              hover:text-purple-600"
            :aria-label="t('MoreOptions')"
          >
            <MoreHorizontal
              class="h-5 w-5"
              :stroke-width="1.5"
            />
          </button>
        </PopoverTrigger>
        <PopoverContent
          align="start"
          :side-offset="4"
          class="min-h-[52px] w-72 rounded-xl p-0
            shadow-[0px_4px_8px_0px_rgba(0,0,0,0.08),0px_8px_12px_0px_rgba(0,0,0,0.08)]"
        >
          <button
            type="button"
            class="flex w-full items-center gap-2.5 border-b border-gray-50 p-4 text-sm font-normal text-gray-800
              hover:bg-gray-50"
            data-cy="save-snippet-button"
            @click="handleSaveSnippet"
          >
            <Bookmark
              class="h-5 w-5"
              :stroke-width="1.25"
            />
            {{ t('SaveResponse') }}
          </button>
          <button
            type="button"
            class="flex w-full items-center gap-2.5 p-4 text-sm font-normal text-gray-800 hover:bg-gray-50"
            @click="handleDownload"
          >
            <ArrowDownToLine
              class="h-5 w-5"
              :stroke-width="1.25"
            />
            {{ t('Download') }}
          </button>
        </PopoverContent>
      </Popover>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
  import { useTranslation } from '@/composables/useTranslation'
  import { type AIChatSession, type AIChatSessionMessage, FeatureEnum } from '@/types/generated'
  import { FeedbackService } from '@/types/generated/services.gen'
  import {
    ArrowDownToLine,
    Bookmark,
    Check,
    Copy,
    MoreHorizontal,
    ThumbsDown,
    ThumbsUp
  } from 'lucide-vue-next'
  import { computed, inject, ref, type Ref } from 'vue'

  const props = defineProps<{
    message: AIChatSessionMessage
  }>()

  const emit = defineEmits<{
    'save-snippet': []
    rated: [{ value: 'positive' | 'negative'; feedback_uuid?: string }]
  }>()

  const { t } = useTranslation({
    en: {
      Helpful: 'Helpful',
      NotHelpful: 'Not helpful',
      MarkedHelpful: 'Marked as helpful',
      MarkedNotHelpful: 'Marked as not helpful',
      Download: 'Download',
      Copy: 'Copy',
      CopyToClipboard: 'Copy to clipboard',
      Copied: 'Copied!',
      SaveResponse: 'Save snippet',
      MoreOptions: 'More options'
    },
    de: {
      Helpful: 'Hilfreich',
      NotHelpful: 'Nicht hilfreich',
      MarkedHelpful: 'Als hilfreich markiert',
      MarkedNotHelpful: 'Als nicht hilfreich markiert',
      Download: 'Herunterladen',
      Copy: 'Kopieren',
      CopyToClipboard: 'In Zwischenablage kopieren',
      Copied: 'Kopiert!',
      SaveResponse: 'Snippet speichern',
      MoreOptions: 'Weitere Optionen'
    }
  })

  const rating = ref<'positive' | 'negative' | null>(null)
  const copied = ref(false)
  const morePopoverOpen = ref(false)
  const copyButtonTitle = computed(() => (copied.value ? t('Copied') : t('CopyToClipboard')))

  const activeChatSession = inject<Ref<AIChatSession | null>>('activeChatSession', ref(null))

  const handleRating = async (value: 'positive' | 'negative') => {
    if (rating.value === value) {
      rating.value = null
      return
    }

    rating.value = value

    try {
      const response = await FeedbackService.feedbackCreate({
        requestBody: {
          rating: value === 'positive' ? '5.0' : '0.0',
          feature: FeatureEnum.AI_CHAT,
          url: window.location.href,
          related_data: {
            ai_chat: {
              ai_chat_session_uuid: activeChatSession.value?.uuid,
              ai_chat_session_message_uuid: props.message.uuid
            }
          }
        }
      })

      // Emit the rating event with feedback UUID
      emit('rated', {
        value,
        feedback_uuid: response.uuid
      })
    } catch (error) {
      console.error('Failed to submit rating:', error)
      // Reset rating on error
      rating.value = null
    }
  }

  const handleSaveSnippet = () => {
    morePopoverOpen.value = false
    emit('save-snippet')
  }

  const handleDownload = () => {
    morePopoverOpen.value = false

    // Extract text content from message
    let textContent = ''

    if (typeof props.message.content === 'string') {
      textContent = props.message.content
    } else if (props.message.content?.chunks) {
      const chunks = Array.isArray(props.message.content.chunks)
        ? (props.message.content.chunks as any[])
        : []

      textContent = chunks
        .filter((chunk) => chunk?.type === 'text')
        .map((chunk) => chunk.text || '')
        .join('')
    }

    // Create and download file
    const blob = new Blob([textContent], { type: 'text/plain' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `ai-response-${new Date().toISOString().slice(0, 10)}.txt`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
  }

  const copyToClipboard = async () => {
    try {
      // Extract text content from message
      let textContent = ''

      if (typeof props.message.content === 'string') {
        textContent = props.message.content
      } else if (props.message.content?.chunks) {
        const chunks = Array.isArray(props.message.content.chunks)
          ? (props.message.content.chunks as any[])
          : []

        textContent = chunks
          .filter((chunk) => chunk?.type === 'text')
          .map((chunk) => chunk.text || '')
          .join('')
      }

      await navigator.clipboard.writeText(textContent)
      copied.value = true
      setTimeout(() => {
        copied.value = false
      }, 2000)
    } catch (error) {
      console.error('Failed to copy to clipboard:', error)
    }
  }
</script>

<style scoped>
  .fade-enter-active,
  .fade-leave-active {
    transition: opacity 0.2s ease;
  }

  .fade-enter-from,
  .fade-leave-to {
    opacity: 0;
  }
</style>
